import React, { useRef, useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';
import { FaTimes } from 'react-icons/fa';
import { ReactComponent as ImageAddIcon } from '../../assets/svg/ImageAddIcon.svg';
import '../../styles/modals/StudentInfoModal.css';
import { Controller, useForm } from 'react-hook-form';
import Loading from '../Loading';
import { useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { createUpdateCourse, getCourseCategories, getLocales, uploadFile } from '../../server/server';
import CategoriesDropDown from './CategoriesDropDown';
import OtherCategoriesDropDown from './OtherCategoriesDropDown';
import { fileSystemUrl } from '../../server/connections';
import { checkAllowedExtensions, notify } from '../../utils/util';

function CreateEditCourseModal({ modalIsOpen, closeModal, course = null, reloadCourse }: { modalIsOpen: any; closeModal: any; course: any; reloadCourse?: any }) {
	const [processing, setProcessing] = useState(false);
	const navigate = useNavigate();
	const [image, setImage] = useState<string | undefined>(undefined);
	const [selectedFile, setSelectedFile] = useState(undefined);
	const [removedPhoto, setRemovedPhoto] = useState(false);
	const [onboardingImage, setOnboardingImage] = useState<string | undefined>(undefined);
	const [selectedOnboardingFile, setSelectedOnboardingFile] = useState(undefined);
	const [removedOnboardingPhoto, setRemovedOnboardingPhoto] = useState(false);
	const [categories, setCategories] = useState([]);
	const [otherCategories, setOtherCategories] = useState([]);
	const [locales, setLocales] = useState([]);
	const [mainCategory, setMainCategory] = useState('Select');
	const [locale, setLocale] = useState('Select');

	const { register, handleSubmit, watch, reset, formState, getValues, setValue, control } = useForm({
		defaultValues: {
			title: '',
			subtitle: '',
			challangeTitle: '',
			visible: true,
			localeId: '',
			mainCategoryId: '',
		},
	});
	const { isDirty, isValid, errors } = formState;

	useEffect(() => {
		if (modalIsOpen) {
			loadCategories();
			loadLocales();
		}
	}, [modalIsOpen]);

	useEffect(() => {
		const element = document.getElementById('home');
		ReactModal.setAppElement('#root');
		if (modalIsOpen) {
			if (!!element) element.style.filter = 'blur(5px)';
			if (!!course?.id) {
				setValue('title', course?.title);
				setValue('subtitle', course?.subtitle);
				setValue('challangeTitle', course?.challangeTitle);
				setValue('mainCategoryId', course.mainCategory?.id);
				setMainCategory(course.mainCategory?.name ?? 'Select');
				setValue('localeId', course.locale?.id);
				setLocale(course.locale?.name ?? 'Select');
				setOtherCategoriesList();
			}
			if (!!course?.headlineImage) {
				setImage(fileSystemUrl + '/' + course.headlineImage?.url);
			}
			if (!!course?.onboardingImage) {
				setOnboardingImage(fileSystemUrl + '/' + course.onboardingImage?.url);
			}
		} else {
			if (!!element) element.style.filter = 'unset';
		}
	}, [modalIsOpen]);

	useEffect(() => {
		if (!selectedFile) {
			setImage(undefined);
			console.log('undefined img');
			return;
		}
		console.log('set img');
		const objectUrl = URL.createObjectURL(selectedFile);
		setImage(objectUrl);
		setRemovedPhoto(false);
		return () => {
			console.log('revoke img ');
			URL.revokeObjectURL(objectUrl);
		};
	}, [selectedFile]);

	useEffect(() => {
		if (!selectedOnboardingFile) {
			setOnboardingImage(undefined);
			console.log('undefined onboarding img');
			return;
		}
		console.log('set onboarding img');
		const objectUrl = URL.createObjectURL(selectedOnboardingFile);
		setOnboardingImage(objectUrl);
		setRemovedOnboardingPhoto(false);
		return () => {
			console.log('revoke onboarding img ');
			URL.revokeObjectURL(objectUrl);
		};
	}, [selectedOnboardingFile]);

	const setOtherCategoriesList = () => {
		let newList: any = otherCategories.map((item: any) => {
			course.otherCategories.forEach((cat: any) => {
				if (item.id === cat.category?.id) {
					item.value = cat.value;
				}
			});
			return item;
		});
		setCategories(newList);
	};

	const loadCategories = async () => {
		const resp = await getCourseCategories();
		if (resp.status === 200) {
			setCategories(resp.data);
			let newList = resp.data?.map((item: any) => {
				item.value = undefined;
				return item;
			});
			console.log('other categories ', newList);
			setOtherCategories(newList);
		}
	};
	const loadLocales = async () => {
		const resp = await getLocales();
		if (resp.status === 200) {
			setLocales(resp.data);
		}
	};

	const uploadAndGetFile = async (file: File | undefined) => {
		if (!file) return null;
		const resp = await uploadFile({ file });
		if (resp.status === 201) {
			return resp.data;
		}
		return null;
	};

	const onSubmit = async () => {
		console.log('values ', getValues());

		const headlineFile = await uploadAndGetFile(selectedFile);
		const onboardingFile = await uploadAndGetFile(selectedOnboardingFile);

		let headlineFileId: string | null = null;
		if (headlineFile) {
			headlineFileId = headlineFile.id;
		} else if (!!course?.headlineImage && !removedPhoto) {
			headlineFileId = course.headlineImage.id;
		} else if ((!!course?.headlineImage && removedPhoto) || !course?.headlineImage) {
			headlineFileId = null;
		}

		let onboardingFileId: string | null = null;
		if (onboardingFile) {
			onboardingFileId = onboardingFile.id;
		} else if (!!course?.onboardingImage && !removedOnboardingPhoto) {
			onboardingFileId = course.onboardingImage.id;
		} else if ((!!course?.onboardingImage && removedOnboardingPhoto) || !course?.onboardingImage) {
			onboardingFileId = null;
		}

		if (course === null) {
			createCourse(headlineFileId, onboardingFileId);
		} else {
			updateCourse(headlineFileId, onboardingFileId);
		}
	};

	const createCourse = async (headlineFileId: string | null, onboardingFileId: string | null) => {
		setProcessing(true);
		let newCourse: any = getValues();
		newCourse.headlineImageId = headlineFileId;
		newCourse.onboardingImageId = onboardingFileId;

		let newList: any[] = otherCategories.filter((item: any) => !!item.value);
		newList = newList.map((item: any) => ({
			categoryId: item.id,
			value: isNaN(parseInt(item.value)) ? 0 : parseInt(item.value),
		}));
		newCourse.otherCategories = newList;

		const resp = await createUpdateCourse(newCourse);
		if (resp.status === 201) {
			const courseFresh = resp.data;
			navigate(`/courses/${courseFresh.id}`);
			closeModal();
			notify('Course is created');
		}
		setProcessing(false);
	};

	const updateCourse = async (headlineFileId: string | null, onboardingFileId: string | null) => {
		setProcessing(true);
		let newCourse: any = getValues();
		newCourse.id = course?.id;
		newCourse.headlineImageId = headlineFileId;
		newCourse.onboardingImageId = onboardingFileId;

		let newList: any[] = otherCategories.filter((item: any) => !!item.value);
		newList = newList.map((item: any) => ({
			categoryId: item.id,
			value: isNaN(parseInt(item.value)) ? 0 : parseInt(item.value),
		}));
		newCourse.otherCategories = newList;

		const resp = await createUpdateCourse(newCourse);
		if (resp.status === 201) {
			const courseFresh = resp.data;
			reloadCourse();
			closeModal();
			notify('Course is updated');
		}
		setProcessing(false);
	};

	const press_upload = () => {
		document.getElementById('photo_upload_modal')?.click();
	};

	const press_onboarding_upload = () => {
		document.getElementById('onboarding_photo_upload_modal')?.click();
	};

	const closeImage = () => {
		setImage(undefined);
		setSelectedFile(undefined);
		setRemovedPhoto(true);
	};

	const closeOnboardingImage = () => {
		setOnboardingImage(undefined);
		setSelectedOnboardingFile(undefined);
		setRemovedOnboardingPhoto(true);
	};

	const onImageChange = (e: any) => {
		console.log(image);
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(undefined);
			return;
		}
		let file = e.target.files[0];
		if (file.size > 15728640) {
			alert('File is too big!');
			setSelectedFile(undefined);
			return;
		}
		if (!checkAllowedExtensions(file.name)) {
			alert('Type of file is not allowed!');
			return;
		}
		console.log('Selected file : ', file);
		setSelectedFile(file);
	};
	const resetInputValue = (e: any) => {
		e.target.value = null;
	};

	const onOnboardingImageChange = (e: any) => {
		console.log(onboardingImage);
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedOnboardingFile(undefined);
			return;
		}
		let file = e.target.files[0];
		if (file.size > 15728640) {
			alert('File is too big!');
			setSelectedOnboardingFile(undefined);
			return;
		}
		if (!checkAllowedExtensions(file.name)) {
			alert('Type of file is not allowed!');
			return;
		}
		console.log('Selected file : ', file);
		setSelectedOnboardingFile(file);
	};
	const resetOnboardingInputValue = (e: any) => {
		e.target.value = null;
	};

	const cleanUpFunction = () => {
		reset();
		setImage(undefined);
		setSelectedFile(undefined);
		setRemovedPhoto(false);
		setOnboardingImage(undefined);
		setSelectedOnboardingFile(undefined);
		setRemovedOnboardingPhoto(false);
		setProcessing(false);
		setMainCategory('Select');
		setLocale('Select');
	};

	const setSelectedMainCategoryId = (item: any) => {
		setValue('mainCategoryId', item?.id, { shouldValidate: true });
		setMainCategory(!!item?.name ? item.name : 'Select');
	};

	const setSelectedLocaleId = (item: any) => {
		setValue('localeId', item?.id, { shouldValidate: true });
		setLocale(!!item?.name ? item.name : 'Select');
	};

	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterOpen={() => {}}
			onAfterClose={() => cleanUpFunction()}
			onRequestClose={closeModal}
			style={{
				overlay: {
					zIndex: 0,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 900,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className={'scrollbar-display-none'}
		>
			<div className='student-info-modal  '>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon ' onClick={() => closeModal()}>
						<CloseModalIcon />
					</div>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]'>
					<div className='flex flex-row items-center'>
						<div className='flex-1'>
							<p className='text-primaryViolet font-semibold text-3xl text-left'>{course === null ? 'Create new course' : 'Edit course'}</p>
						</div>
					</div>
					<div>
						<div className='mt-5'>
							<p className='text-primaryViolet font-semibold text-lg text-left'>Title</p>
							<div className={`border-b  p-2 mt-2 flex flex-row items-center ${errors.title?.type === 'required' ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
								<input
									className={`w-full text-primaryViolet ${errors.title?.type === 'required' ? 'placeholder:text-redError' : ''}`}
									placeholder={`Type course title`}
									{...register('title', { required: true })}
									style={{ backgroundColor: 'transparent' }}
								/>
								<p className='text-redError   text-nowrap'>{errors.title?.type === 'required' ? '*This field is required' : ''}</p>
							</div>
						</div>
						<div className='mt-4'>
							<div className='flex flex-row items-end justify-between gap-5'>
								<p className={`text-primaryViolet font-semibold text-lg text-left ${errors.subtitle?.type === 'required' ? 'text-redError' : 'text-primaryViolet'}`}>Subtitle</p>

								<p className='text-redError font-medium text-lg  '>{errors.subtitle?.type === 'required' ? '* This field is required' : ''}</p>
							</div>
							<Controller
								control={control}
								name='subtitle'
								rules={{ required: true }}
								render={({ field }) => <MDEditor data-color-mode='light' value={field.value} onChange={(value: any) => setValue('subtitle', value, { shouldValidate: true })} preview='edit' />}
							/>
						</div>
						<div className='mt-4'>
							<div className='flex flex-row items-end justify-between gap-5'>
								<p className={`text-primaryViolet font-semibold text-lg text-left ${errors.challangeTitle?.type === 'required' ? 'text-redError' : 'text-primaryViolet'}`}>Challange Title</p>

								<p className='text-redError font-medium text-lg  '>{errors.challangeTitle?.type === 'required' ? '* This field is required' : ''}</p>
							</div>
							<Controller
								control={control}
								name='challangeTitle'
								rules={{ required: true }}
								render={({ field }) => <MDEditor data-color-mode='light' value={field.value} onChange={(value: any) => setValue('challangeTitle', value, { shouldValidate: true })} preview='edit' />}
							/>
						</div>
						<div className='mt-4'>
							<CategoriesDropDown
								list={categories}
								register={register}
								registerField='mainCategoryId'
								errors={errors.mainCategoryId?.type}
								label='Categories'
								selectedValue={mainCategory}
								setSelectedValue={setSelectedMainCategoryId}
							/>
						</div>
						<CategoriesDropDown
							list={locales}
							register={register}
							registerField='localeId'
							errors={errors.localeId?.type}
							label='Locales'
							selectedValue={locale}
							setSelectedValue={setSelectedLocaleId}
						/>
						{/* 						
						<OtherCategoriesDropDown list={otherCategories} label='Other categories' setOtherCategories={setOtherCategories} />
						 */}
						<div className='flex flex-col justify-center items-center mt-5'>
							{!image && (
								<div className='flex flex-col items-center justify-center bg-greyBackground rounded-xl py-8 w-[334px]'>
									<ImageAddIcon style={{ width: 42, height: 42, color: '#8484b1' }} />
									<p className='text-mudPurple font-medium text-[16px] left-5 text-center w-[160px]'>The maximum image size for download is 1400px x 1400px</p>
									<button
										className='text-primaryViolet rounded-full bg-greyLight py-1 font-semibold text-[18px] left-6 px-5 mt-3'
										onClick={(e) => {
											e.preventDefault();
											press_upload();
										}}
									>
										Add Photo
									</button>
								</div>
							)}
							{image && (
								<div className='flex relative max-h-[244px] flex-col items-center justify-center bg-greyBackground rounded-xl pt-10 pb-4 w-[334px]'>
									<div onClick={() => closeImage()} className='bg-white opacity-40 p-2 rounded-full cursor-pointer border border-greyLight absolute top-[8px] right-[8px] hover:border-greyMedium'>
										<FaTimes style={{ width: 20, height: 20, stroke: 'white', display: 'block' }} />
									</div>

									<img style={{ objectFit: 'contain', width: '100%', minHeight: 170, marginTop: 10 }} src={image} alt='' />
								</div>
							)}
							<input title='Add Video/Photo' style={{ display: 'none' }} accept='video/*, image/*' type='file' id='photo_upload_modal' onChange={onImageChange} onClick={resetInputValue} />
						</div>
						<div className='flex flex-col justify-center items-center mt-5'>
							{!onboardingImage && (
								<div className='flex flex-col items-center justify-center bg-greyBackground rounded-xl py-8 w-[334px]'>
									<ImageAddIcon style={{ width: 42, height: 42, color: '#8484b1' }} />
									<p className='text-mudPurple font-medium text-[16px] left-5 text-center w-[160px]'>The maximum image size for download is 1400px x 1400px</p>
									<button
										className='text-primaryViolet rounded-full bg-greyLight py-1 font-semibold text-[18px] left-6 px-5 mt-3'
										onClick={(e) => {
											e.preventDefault();
											press_onboarding_upload();
										}}
									>
										Add Onboarding Photo
									</button>
								</div>
							)}
							{onboardingImage && (
								<div className='flex relative max-h-[244px] flex-col items-center justify-center bg-greyBackground rounded-xl pt-10 pb-4 w-[334px]'>
									<div
										onClick={() => closeOnboardingImage()}
										className='bg-white opacity-40 p-2 rounded-full cursor-pointer border border-greyLight absolute top-[8px] right-[8px] hover:border-greyMedium'
									>
										<FaTimes style={{ width: 20, height: 20, stroke: 'white', display: 'block' }} />
									</div>

									<img style={{ objectFit: 'contain', width: '100%', minHeight: 170, marginTop: 10 }} src={onboardingImage} alt='' />
								</div>
							)}
							<input
								title='Add Video/Photo'
								style={{ display: 'none' }}
								accept='video/*, image/*'
								type='file'
								id='onboarding_photo_upload_modal'
								onChange={onOnboardingImageChange}
								onClick={resetOnboardingInputValue}
							/>
						</div>
					</div>
					<div className='flex flex-row justify-end w-full gap-5'>
						{/* <div
							className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-[#fafaff]  h-[60px] button-transparent-class'
							onClick={closeModal}
						>
							<p className='text-[18px] text-primaryViolet leading-5 font-semibold mr-3 '>Back</p>
						</div> */}
						<button
							className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer  rounded-full bg-primaryViolet  min-h-[60px] button-save-class transition'
							disabled={processing}
						>
							{processing ? (
								<Loading primaryColor='#FDDF6D' secondaryColor='#5E639B' size={25} stroke={7} />
							) : (
								<div className='flex flex-row items-center justify-center'>
									<p className='text-[18px]  leading-5 font-semibold mr-3 '>Save</p>
									<Save />
								</div>
							)}
						</button>
					</div>
				</form>
			</div>
		</ReactModal>
	);
}

export default CreateEditCourseModal;
